import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getOneCMSContentKey } from "./useGetOneContent";
import { getPuckPageQueryKey } from "./useGetPuckPage";
import { PuckPage } from "./useCreatePuckPage";
import { z } from "zod";

type UpdatePuckPageOptions = {
    slug: string;
    data: {
        title?: string;
        content?: string;
    };
};

export const useUpdatePuckPage = () => {
    const queryClient = useQueryClient();

    return useMutation<PuckPage, Error, UpdatePuckPageOptions>({
        mutationFn: async (options: UpdatePuckPageOptions) => {
            try {
                const validatedOptions = UpdatePuckPageOptionsSchema.parse(options);
                const url = `/api/cms/puck/page/${validatedOptions.slug}`;

                const response = await loggedInApiCall.patch<PuckPage>(url, validatedOptions.data, {
                    headers: {
                        "Content-Type": "application/ld+json",
                    },
                });

                return PuckUpdateResponseSchema.parse(response.data);
            } catch (error) {
                console.error(`Error updating Puck page (${options.slug}):`, error);

                if (error instanceof Error) {
                    throw error;
                } else {
                    throw new Error("Failed to update Puck page: Unknown error");
                }
            }
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries([getOneCMSContentKey]);
            queryClient.invalidateQueries([getPuckPageQueryKey, variables.slug]);
        },
    });
};

export const UpdatePuckPageOptionsSchema: z.ZodSchema<UpdatePuckPageOptions> = z.object({
    slug: z.string(),
    data: z.object({
        title: z.string().optional(),
        content: z.string().optional(),
    }),
});

export const PuckUpdateResponseSchema: z.ZodSchema<PuckPage> = z.object({
    slug: z.string(),
    title: z.string(),
    content: z.string(),
    locale: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
